import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronRight,
  ArrowRight,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { fetchSeoMedicationIndex } from "../lib/api";
import SeoHead from "../components/SeoHead";

const RISK_VISUAL = {
  HIGH:     { chip: "bg-rose-100 text-rose-800 border-rose-200",       Icon: AlertCircle    },
  MODERATE: { chip: "bg-amber-100 text-amber-900 border-amber-200",    Icon: AlertTriangle  },
  LOW:      { chip: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
};

const DISCLAIMER =
  "This information is for educational purposes only and is not a substitute for advice from your prescribing physician or dermatologist.";

function Skeleton() {
  return (
    <main className="bg-cream-50 min-h-[80vh]">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 space-y-6 animate-pulse">
        <div className="h-3 w-1/3 bg-cream-200 rounded" />
        <div className="h-12 w-full bg-cream-200 rounded" />
        <div className="h-32 w-full bg-cream-200 rounded-2xl" />
        <div className="h-72 w-full bg-cream-200 rounded-2xl" />
      </div>
    </main>
  );
}

function NotFound() {
  return (
    <main className="bg-cream-50 min-h-[80vh] flex items-center justify-center px-5">
      <div
        data-testid="seo-medication-not-found"
        className="max-w-md text-center space-y-5"
      >
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] text-ink">
          No interactions found for this medication.
        </h1>
        <p className="text-ink-soft">
          Run a personalized check on any product to see how it interacts with
          your medication.
        </p>
        <Link
          to="/check"
          className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Check a product <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </main>
  );
}

export default function MedicationInteractionsPage() {
  const params = useParams();
  const medication = params.medication || params.combined;
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    (async () => {
      try {
        const res = await fetchSeoMedicationIndex(medication);
        if (cancelled) return;
        setData(res);
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        setStatus(e?.response?.status === 404 ? "not_found" : "not_found");
      }
    })();
    return () => { cancelled = true; };
  }, [medication]);

  if (status === "loading") return <Skeleton />;
  if (status === "not_found" || !data) return <NotFound />;

  const { medication: med, interactions, meta } = data;
  const generic = med.generic_name || (med.brand_names && med.brand_names[0]) || "this medication";
  const primaryBrand = (med.brand_names && med.brand_names[0]) || generic;
  const medLabel = primaryBrand && primaryBrand !== generic ? `${generic} (${primaryBrand})` : generic;
  const brandList = (med.brand_names || []).join(" / ") || generic;

  return (
    <main data-testid="seo-medication-page" className="bg-cream-50 min-h-[80vh] overflow-x-clip">
      <SeoHead
        title={meta.title}
        description={meta.description}
        canonicalPath={meta.canonical_url}
      />

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {/* Breadcrumb */}
        <nav
          data-testid="seo-medication-breadcrumb"
          className="flex items-center gap-1.5 text-[12.5px] text-ink-mute mb-6 flex-wrap"
        >
          <Link to="/" className="hover:text-ink transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5 text-ink-light" />
          <Link to="/browse" className="hover:text-ink transition-colors">Interactions</Link>
          <ChevronRight className="w-3.5 h-3.5 text-ink-light" />
          <span className="text-ink">{primaryBrand}</span>
        </nav>

        {/* H1 */}
        <h1
          data-testid="seo-medication-h1"
          className="font-bold text-3xl sm:text-4xl md:text-5xl tracking-[-0.025em] text-ink leading-[1.05]"
        >
          Skincare Ingredients and {medLabel}: Complete Interaction Guide
        </h1>

        {/* Intro */}
        <p
          data-testid="seo-medication-intro"
          className="mt-6 text-[15.5px] sm:text-[17px] text-ink-soft leading-relaxed max-w-3xl"
        >
          If you're taking {generic} ({brandList}), knowing which skincare
          ingredients have been checked against the label is critical. Here's what the clinical literature
          says about {generic} and common skincare ingredients.
        </p>

        {/* Interaction table */}
        <section data-testid="seo-medication-list" className="mt-10 rounded-3xl bg-white border border-surface-line overflow-hidden">
          <div className="hidden sm:grid grid-cols-[1.4fr_0.7fr_2fr_auto] gap-4 px-6 py-4 border-b border-surface-line bg-cream-100/60 text-[10.5px] uppercase tracking-[0.18em] font-semibold text-ink-mute">
            <span>Ingredient</span>
            <span>Risk Level</span>
            <span>Summary</span>
            <span className="text-right">Check</span>
          </div>
          <ul>
            {interactions.map((row) => {
              const v = RISK_VISUAL[row.risk_level] || RISK_VISUAL.LOW;
              const Icon = v.Icon;
              return (
                <li
                  key={row.url}
                  data-testid={`seo-row-${row.ingredient_id}`}
                  className="grid grid-cols-1 sm:grid-cols-[1.4fr_0.7fr_2fr_auto] gap-2 sm:gap-4 px-6 py-5 border-b last:border-b-0 border-surface-line hover:bg-cream-50 transition-colors"
                >
                  <div className="font-bold text-[15px] text-ink flex items-center gap-2">
                    <Icon className="w-4 h-4 sm:hidden" strokeWidth={1.8} />
                    {row.ingredient_name}
                  </div>
                  <div>
                    <span
                      className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold border rounded-full px-2.5 py-1 ${v.chip}`}
                    >
                      <Icon className="w-3 h-3 hidden sm:inline" strokeWidth={2.2} />
                      {row.risk_level}
                    </span>
                  </div>
                  <p className="text-[13.5px] text-ink-soft leading-snug">
                    {row.summary || row.verdict_label}
                  </p>
                  <div className="sm:text-right">
                    <Link
                      to={row.url}
                      className="inline-flex items-center gap-1 text-[13px] font-bold text-ink hover:text-peach-500 transition-colors"
                    >
                      View <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        {/* CTA */}
        <section
          data-testid="seo-medication-cta"
          className="mt-12 rounded-3xl bg-white border border-surface-line p-6 sm:p-8"
        >
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-ink-mute font-semibold">
            Run a personalized check
          </div>
          <h3 className="mt-2 font-bold text-xl sm:text-2xl tracking-[-0.02em] text-ink">
            Check your full skincare routine
          </h3>
          <p className="mt-2 text-[14.5px] text-ink-soft leading-relaxed max-w-xl">
            See how all your products interact with {generic} — a clear,
            source-linked verdict for every ingredient.
          </p>
          <Link
            to={`/check?med=${encodeURIComponent(medication)}`}
            className="mt-5 inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Check a product <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        {/* Disclaimer */}
        <p
          data-testid="seo-medication-disclaimer"
          className="mt-10 text-[12.5px] text-ink-mute italic leading-relaxed"
        >
          {DISCLAIMER}
        </p>
      </div>
    </main>
  );
}
